/**
 * Auth API — real backend calls.
 *
 * See BACKEND_CONTRACT.md for the full endpoint specifications.
 * Used when VITE_USE_MOCKS=false.
 */

import axiosInstance from './axiosInstance';

/**
 * Request an OTP to be sent to the user's phone.
 *
 * BACKEND: POST /auth/request-otp
 * BODY:    { phone: "+989..." }
 * SUCCESS: { data: { expiresIn: 120, resendAvailableIn: 60 } }
 * ERRORS:  429 RESEND_TOO_SOON (retryAfter in details), 400 INVALID_PHONE
 *
 * @param {string} phone - E.164 format
 * @returns {Promise<{ expiresIn: number, resendAvailableIn: number }>}
 */
export async function requestOtp(phone) {
  const res = await axiosInstance.post('/auth/request-otp', { phone });
  return res.data.data;
}

/**
 * Verify the OTP code and receive tokens.
 *
 * BACKEND: POST /auth/verify-otp
 * BODY:    { phone: "+989...", code: "12345" }
 * SUCCESS: { data: { accessToken: "...", isNewUser: boolean, user: object|null } }
 *          + sets refresh_token httpOnly cookie
 * ERRORS:  400 INVALID_OTP (attemptsRemaining in details), 423 LOCKED
 *
 * @param {string} phone - E.164 format
 * @param {string} code - OTP code
 * @returns {Promise<{ accessToken: string, isNewUser: boolean, user: object|null }>}
 */
export async function verifyOtp(phone, code) {
  const res = await axiosInstance.post('/auth/verify-otp', { phone, code });
  return res.data.data;
}

/**
 * Refresh the access token using the refresh cookie.
 * Note: this is also called automatically by the Axios interceptor on 401.
 *
 * BACKEND: POST /auth/refresh
 * BODY:    (none — uses cookie)
 * SUCCESS: { data: { accessToken: "..." } }
 * ERRORS:  401 (cookie missing/expired) → user must log in again
 *
 * @returns {Promise<{ accessToken: string }>}
 */
export async function refreshToken() {
  // `role` tells the backend WHICH session to refresh. It keeps one per
  // console, so a phone signed into this app and (say) the clinic panel on the
  // same browser no longer refreshes whichever was signed into last.
  const res = await axiosInstance.post('/auth/refresh', { role: 'user' });
  // Read both shapes: this is the one auth endpoint that envelopes its body,
  // and reading only one of them is what made a valid session look expired in
  // the sibling consoles.
  const body = res.data;
  const accessToken = body?.accessToken ?? body?.data?.accessToken ?? null;
  return { ...(body?.data ?? {}), accessToken };
}

/**
 * Log out — invalidates the session on the backend.
 *
 * BACKEND: POST /auth/logout
 * SUCCESS: { data: { success: true } }
 *          + clears refresh_token cookie
 *
 * @returns {Promise<void>}
 */
export async function logoutRequest() {
  // Name the role so this ends THIS app's session only — a client who also
  // uses the clinic panel on the same browser should not be signed out of it.
  await axiosInstance.post('/auth/logout', { role: 'user' });
}

export const authApi = {
  requestOtp,
  verifyOtp,
  refreshToken,
  logoutRequest,
};

export default authApi;
